import {
  ASSESSMENTS_FILTER_KEYS,
  DefaultSchoolYear,
  EvaluationType,
  FilterKeys,
  FilterSection,
  GetAssessmentsQueryFilters,
  RepetitionType,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AssessmentRecurrenceExpandedSelect,
  AssessmentRecurrenceTagSelect,
  AssessmentStatusExpandedSelect,
  AssessmentStatusTagSelect,
  DateRangeDropdown,
  EvaluationTypeExpandedSelect,
  FilterDropdown,
  FiltersContainer,
  FiltersSidebarMethods,
  GroupExpandedSelect,
  GroupTagSelect,
  PersonalFiltersDropdown,
  pickOnlyParamsFromFilterKeys,
  renderAreaOfLearningTags,
  ReportExpandedSelect,
  ReportTagSelect,
  SubjectExpandedSelect,
  SubjectTagSelect,
  UserExpandedSelect,
  UserTagSelect,
} from '@schooly/components/filters';
import { TagSelect } from '@schooly/style';
import { FC, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import { AssessmentsFiltersSidebar } from './AssessmentsFiltersSidebar';
import { useAssessmentsFilters } from './useAssessmentsFilters';

type AssessmentsFiltersProps = {
  schoolId: string;
  filters: GetAssessmentsQueryFilters;
  defaultFilters: GetAssessmentsQueryFilters;
  onSetFilters: (v: GetAssessmentsQueryFilters) => void;
  notActualInitialDate?: boolean;
  defaultSchoolYear?: DefaultSchoolYear;
  defaultUserFilters: GetAssessmentsQueryFilters;
};

export const AssessmentsFilters: FC<AssessmentsFiltersProps> = ({
  defaultFilters,
  onSetFilters,
  filters,
  schoolId,
  notActualInitialDate,
  defaultSchoolYear,
}) => {
  const sidebar = useRef<FiltersSidebarMethods | null>(null);
  const personalFiltersDropdown = useRef<PersonalFiltersDropdown | null>(null);
  const { currentStaff } = useAuth();
  const { formatMessage } = useIntl();

  const {
    labels,
    filtersDate,
    filtersAssessmentStatus,
    filtersGroup,
    filtersReport,
    filtersSubject,
    filtersStaff,
    filtersRecurrenceId,
    filtersEvaluationType,
    filtersAreaOfLearning,
    onSetDate,
    onSetAssessmentStatus,
    onClearAssessmentStatus,
    onSetGroup,
    onClearGroup,
    onSetReport,
    onClearReport,
    onSetSubject,
    onClearSubject,
    onSetStaff,
    onClearStaff,
    onSetRecurrenceId,
    onClearRecurrenceId,
    onSetEvaluationType,
    onClearEvaluationType,
    onSetAreaOfLearning,
  } = useAssessmentsFilters({ filters, onSetFilters });

  const handleSaveFilter = useCallback(() => {
    personalFiltersDropdown.current?.saveFilter();
  }, []);

  const dateFilter = { [FilterKeys.Date]: filtersDate };
  const recurringFilter = {
    [FilterKeys.Date]: filtersDate,
    [FilterKeys.RepetitionType]: [RepetitionType.FirstOfRecurrence],
  };

  const isTutorFeedbackSelected = filtersEvaluationType?.includes(EvaluationType.TutorFeedback);

  return (
    <FiltersContainer onOpenFilters={() => sidebar.current?.open()}>
      <PersonalFiltersDropdown
        ref={personalFiltersDropdown}
        currentUser={currentStaff}
        relationId={currentStaff?.relation_id || ''}
        onSaveFilter={handleSaveFilter}
        schoolId={schoolId}
        section={FilterSection.Assessment}
        filters={filters}
        defaultSchoolYear={defaultSchoolYear}
        onSetFilters={(v) => {
          onSetFilters(pickOnlyParamsFromFilterKeys(ASSESSMENTS_FILTER_KEYS, v));
        }}
      />

      {filtersDate && (
        <DateRangeDropdown
          schoolId={schoolId}
          date={filtersDate}
          onSetDate={onSetDate}
          openLabel={labels[FilterKeys.Date]}
          defaultSchoolYear={defaultSchoolYear}
          notActualInitialDate={notActualInitialDate}
        />
      )}

      {!!filtersAssessmentStatus?.length && (
        <FilterDropdown
          onClear={onClearAssessmentStatus}
          label={labels[FilterKeys.AssessmentStatus]}
          tags={(open) =>
            filtersAssessmentStatus.map((status) => (
              <AssessmentStatusTagSelect
                key={status}
                sx={{ maxWidth: 200 }}
                status={status}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <AssessmentStatusExpandedSelect
              selectedStatuses={filtersAssessmentStatus}
              onApply={onSetAssessmentStatus}
              onClose={onClose}
              onClear={onClearAssessmentStatus}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersGroup?.length && (
        <FilterDropdown
          onClear={onClearGroup}
          label={labels[FilterKeys.Group]}
          width={500}
          tags={(open) =>
            filtersGroup.map((id) => (
              <GroupTagSelect key={id} sx={{ maxWidth: 200 }} id={id} onClick={open} />
            ))
          }
        >
          {(onClose) => (
            <GroupExpandedSelect
              selectedIds={filtersGroup}
              schoolId={schoolId}
              onApply={onSetGroup}
              onClose={onClose}
              onClear={onClearGroup}
              filters={dateFilter}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersReport?.length && (
        <FilterDropdown
          onClear={onClearReport}
          label={labels[FilterKeys.Report]}
          tags={(open) =>
            filtersReport.map((id) => (
              <ReportTagSelect key={id} sx={{ maxWidth: 200 }} id={id} onClick={open} />
            ))
          }
        >
          {(onClose) => (
            <ReportExpandedSelect
              selectedIds={filtersReport}
              schoolId={schoolId}
              onApply={onSetReport}
              onClose={onClose}
              onClear={onClearReport}
              filters={dateFilter}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersSubject?.length && (
        <FilterDropdown
          onClear={onClearSubject}
          label={labels[FilterKeys.Subject]}
          tags={(open) =>
            filtersSubject.map((id) => (
              <SubjectTagSelect
                schoolId={schoolId}
                sx={{ maxWidth: 200 }}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <SubjectExpandedSelect
              schoolId={schoolId}
              selectedIds={filtersSubject}
              onApply={onSetSubject}
              onClose={onClose}
              onClear={onClearSubject}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersStaff?.length && (
        <FilterDropdown
          onClear={onClearStaff}
          label={labels[FilterKeys.Staff]}
          tags={(open) =>
            filtersStaff.map((id) => (
              <UserTagSelect
                userType="staff"
                currentUser={currentStaff}
                sx={{ maxWidth: 200 }}
                schoolId={schoolId}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <UserExpandedSelect
              type="staff"
              schoolId={schoolId}
              currentUser={currentStaff}
              selectedIds={filtersStaff}
              onApply={onSetStaff}
              onClose={onClose}
              onClear={onClearStaff}
              filters={dateFilter}
            />
          )}
        </FilterDropdown>
      )}

      {!!filtersRecurrenceId?.length && (
        <FilterDropdown
          width={500}
          onClear={onClearRecurrenceId}
          label={labels[FilterKeys.RecurrenceId]}
          tags={(open) =>
            filtersRecurrenceId.map((id) => (
              <AssessmentRecurrenceTagSelect
                key={id}
                sx={{ maxWidth: 200 }}
                id={id}
                onClick={open}
                schoolId={schoolId}
                onClear={() => onClearRecurrenceId()}
              />
            ))
          }
        >
          {(onClose) => (
            <AssessmentRecurrenceExpandedSelect
              selectedId={filtersRecurrenceId[0]}
              schoolId={schoolId}
              onSelectRecurrence={(id) => onSetRecurrenceId([id])}
              onClose={onClose}
              onClear={onClearRecurrenceId}
              filters={recurringFilter}
            />
          )}
        </FilterDropdown>
      )}

      {!!(filtersEvaluationType?.length || filtersAreaOfLearning?.length) && (
        <FilterDropdown
          onClear={onClearEvaluationType}
          label={labels[FilterKeys.EvaluationType]}
          tags={(open) => [
            ...(isTutorFeedbackSelected
              ? [
                  <TagSelect
                    key="tutor-feedback"
                    label={formatMessage({ id: 'assessments-TutorsFeedback' })}
                    sx={{ maxWidth: 200 }}
                    onClick={open}
                  />,
                ]
              : []),
            ...renderAreaOfLearningTags({
              ids: filtersAreaOfLearning ?? [],
              onClick: open,
              tagProps: { schoolId, sx: { maxWidth: 200 } },
            }),
          ]}
        >
          {(onClose) => (
            <EvaluationTypeExpandedSelect
              schoolId={schoolId}
              selectedEvaluationTypes={filtersEvaluationType ?? []}
              selectedAreaOfLearningIds={filtersAreaOfLearning ?? []}
              onApply={(type, ids) => {
                onSetEvaluationType(type);
                onSetAreaOfLearning(ids);
              }}
              onClose={onClose}
              onClear={onClearEvaluationType}
            />
          )}
        </FilterDropdown>
      )}

      <AssessmentsFiltersSidebar
        ref={sidebar}
        defaultSchoolYear={defaultSchoolYear}
        defaultFilters={defaultFilters}
        filters={filters}
        onApply={onSetFilters}
        schoolId={schoolId}
      />
    </FiltersContainer>
  );
};
